import {z} from 'zod'
import {captureException} from '@sentry/react'
import axios, {parseAxiosPromise} from '@/axiosInstance.ts'
import {PaginationParams, PaginationResponse} from '@/types'
import {
    SiarBulkExportType,
    SiarExport,
    SiarExportDetail,
    SiarExportPreviewItem,
    SiarExportStatus,
    SiarPreviewStatusFilter
} from '@/features/projects/siar-bulk-export/types'

/* Create */
// Per-status selection block. `selectAll` is the authoritative discriminator:
// true → export every eligible project of the status EXCEPT `exceptedProjectIds`;
// false → export ONLY `selectedProjectIds`. See docs/siar-export-selections-per-status-backend-task.md.
export const SiarStatusSelectionPayload = z.object({
    selectAll: z.boolean(),
    selectedProjectIds: z.array(z.string()),
    exceptedProjectIds: z.array(z.string())
})
export type SiarStatusSelectionPayload = z.infer<typeof SiarStatusSelectionPayload>

export const SiarSelectionsPayload = z.object({
    to_print: SiarStatusSelectionPayload,
    to_export: SiarStatusSelectionPayload,
    to_transmit: SiarStatusSelectionPayload
})
export type SiarSelectionsPayload = z.infer<typeof SiarSelectionsPayload>

export const HttpCreateSiarBulkExportPayload = z.object({
    exportType: SiarBulkExportType,
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    selections: SiarSelectionsPayload,
    addRegistry: z.boolean().optional()
})
export type HttpCreateSiarBulkExportPayload = z.infer<typeof HttpCreateSiarBulkExportPayload>

export type HttpCreateSiarBulkExportResponse = {
    blob: Blob
    exportId: string
    skippedProjectIds: string[]
}

export const NoEligibleProjectsError = z.object({
    code: z.literal('NO_ELIGIBLE_PROJECTS'),
    message: z.string()
})
export type NoEligibleProjectsError = z.infer<typeof NoEligibleProjectsError>

const parseSkippedProjectIdsHeader = (raw: unknown): string[] => {
    if (typeof raw !== 'string' || raw.length === 0) return []
    return raw
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
}

export const httpCreateSiarBulkExport = ({
    payload
}: {
    payload: HttpCreateSiarBulkExportPayload
}): Promise<HttpCreateSiarBulkExportResponse> =>
    axios.post('v1/siar-xml-exports', payload, {responseType: 'blob'}).then(response => ({
        blob: response.data,
        exportId: typeof response.headers['x-export-id'] === 'string' ? response.headers['x-export-id'] : '',
        skippedProjectIds: parseSkippedProjectIdsHeader(response.headers['x-skipped-project-ids'])
    }))

/* Preview */
export type HttpGetSiarBulkExportPreviewParams = {
    exportType: SiarBulkExportType
    dateFrom?: string
    dateTo?: string
    status?: SiarPreviewStatusFilter
    onlyOpeningTransmitted?: boolean
    page?: number
    pageSize?: number
}

export const HttpGetSiarBulkExportPreviewResponse = z.object({
    items: z.array(SiarExportPreviewItem),
    meta: PaginationResponse
})
export type HttpGetSiarBulkExportPreviewResponse = z.infer<typeof HttpGetSiarBulkExportPreviewResponse>

export const httpGetSiarBulkExportPreview = (params: HttpGetSiarBulkExportPreviewParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('v1/siar-xml-exports/preview', {params}),
        responseShape: HttpGetSiarBulkExportPreviewResponse,
        onZodError: captureException
    })

/* Preview counters */
export type HttpGetSiarBulkExportPreviewCountersParams = {
    exportType: SiarBulkExportType
    dateFrom?: string
    dateTo?: string
    onlyOpeningTransmitted?: boolean
}

export const HttpGetSiarBulkExportPreviewCountersResponse = z.object({
    total: z.number(),
    toPrint: z.number().default(0),
    toExport: z.number(),
    toTransmit: z.number(),
    inOtherBatch: z.number(),
    inOtherBatchProjectIds: z.array(z.string()),
    excluded: z.number().default(0)
})
export type HttpGetSiarBulkExportPreviewCountersResponse = z.infer<typeof HttpGetSiarBulkExportPreviewCountersResponse>

export const httpGetSiarBulkExportPreviewCounters = (params: HttpGetSiarBulkExportPreviewCountersParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('v1/siar-xml-exports/preview/counters', {params}),
        responseShape: HttpGetSiarBulkExportPreviewCountersResponse,
        onZodError: captureException
    })

/* List */
export type HttpGetSiarExportHistoryParams = PaginationParams & {
    exportType?: SiarBulkExportType
    dateFrom?: string
    dateTo?: string
    status?: SiarExportStatus
    projectNumber?: string
}

export const HttpGetSiarExportHistoryResponse = z.object({
    items: z.array(SiarExport),
    meta: PaginationResponse
})
export type HttpGetSiarExportHistoryResponse = z.infer<typeof HttpGetSiarExportHistoryResponse>

export const httpGetSiarExportHistory = (params: HttpGetSiarExportHistoryParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('v1/siar-xml-exports', {params}),
        responseShape: HttpGetSiarExportHistoryResponse,
        onZodError: captureException
    })

/* Download */
export const httpReDownloadSiarExport = ({id}: {id: string}): Promise<Blob> =>
    axios.get(`v1/siar-xml-exports/${id}/download`, {responseType: 'blob'}).then(response => response.data)

/* Detail */
export const httpGetSiarExportDetail = ({id}: {id: string}) =>
    parseAxiosPromise({
        axiosPromise: axios.get(`v1/siar-xml-exports/${id}`),
        responseShape: SiarExportDetail,
        onZodError: captureException
    })

/* Remove project from batch */
export type HttpRemoveProjectFromSiarExportPayload = {
    exportId: string
    projectId: string
}

export const HttpRemoveProjectFromSiarExportResponse = z
    .object({
        id: z.string(),
        status: SiarExportStatus,
        projectCount: z.number(),
        transmittedCount: z.number()
    })
    .nullable()
export type HttpRemoveProjectFromSiarExportResponse = z.infer<typeof HttpRemoveProjectFromSiarExportResponse>

export const httpRemoveProjectFromSiarExport = ({
    exportId,
    projectId
}: HttpRemoveProjectFromSiarExportPayload): Promise<HttpRemoveProjectFromSiarExportResponse> =>
    axios.delete(`v1/siar-xml-exports/${exportId}/projects/${projectId}`).then(response => {
        if (response.status === 204) return null
        return HttpRemoveProjectFromSiarExportResponse.parse(response.data)
    })

/* Transmit (selective) */
export const HttpConfirmTransmissionPayload = z.object({
    id: z.string(),
    selectedProjectIds: z.array(z.string()),
    exceptedProjectIds: z.array(z.string()),
    transmissionDate: z.string()
})
export type HttpConfirmTransmissionPayload = z.infer<typeof HttpConfirmTransmissionPayload>

export const HttpConfirmTransmissionResponse = z.object({
    id: z.string(),
    status: SiarExportStatus,
    projectCount: z.number(),
    transmittedCount: z.number()
})
export type HttpConfirmTransmissionResponse = z.infer<typeof HttpConfirmTransmissionResponse>

export const httpConfirmSiarExportTransmission = ({
    id,
    selectedProjectIds,
    exceptedProjectIds,
    transmissionDate
}: HttpConfirmTransmissionPayload): Promise<HttpConfirmTransmissionResponse> =>
    axios
        .post(`v1/siar-xml-exports/${id}/transmit`, {selectedProjectIds, exceptedProjectIds, transmissionDate})
        .then(response => HttpConfirmTransmissionResponse.parse(response.data))
